import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { format, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from "date-fns";
import { 
  Warehouse, 
  Package, 
  Warning, 
  Receipt,
  TrendUp,
  CaretRight,
  Funnel,
  CalendarBlank,
  CurrencyDollar,
  MagnifyingGlass,
  X,
  ArrowUp,
  ArrowDown,
  Trash
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { useNavigate, Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [lowStockSearch, setLowStockSearch] = useState("");

  const filteredLowStockItems = useMemo(() => {
    if (!stats?.low_stock_items) return [];
    if (!lowStockSearch) return stats.low_stock_items;
    return stats.low_stock_items.filter(item => 
      item.name?.toLowerCase().includes(lowStockSearch.toLowerCase())
    );
  }, [stats?.low_stock_items, lowStockSearch]);

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, dateRange, customStartDate, customEndDate]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(response.data);
    } catch (error) {
      console.error("Failed to load locations:", error);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    switch (dateRange) {
      case "today":
        return { start: format(today, "yyyy-MM-dd"), end: format(today, "yyyy-MM-dd") };
      case "week":
        return { start: format(startOfWeek(today), "yyyy-MM-dd"), end: format(endOfWeek(today), "yyyy-MM-dd") };
      case "month":
        return { start: format(startOfMonth(today), "yyyy-MM-dd"), end: format(endOfMonth(today), "yyyy-MM-dd") };
      case "30days":
        return { start: format(subDays(today, 30), "yyyy-MM-dd"), end: format(today, "yyyy-MM-dd") };
      case "90days":
        return { start: format(subDays(today, 90), "yyyy-MM-dd"), end: format(today, "yyyy-MM-dd") };
      case "custom":
        return { 
          start: customStartDate ? format(customStartDate, "yyyy-MM-dd") : null, 
          end: customEndDate ? format(customEndDate, "yyyy-MM-dd") : null 
        };
      default:
        return { start: null, end: null };
    }
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== "custom") {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  const clearFilters = () => {
    setSelectedLocation("all");
    setDateRange("all");
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedLocation !== "all") {
        params.append("location_id", selectedLocation);
      }
      const { start, end } = getDateRange();
      if (start) params.append("start_date", start);
      if (end) params.append("end_date", end);
      
      const url = `${API}/dashboard/stats${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
            DASHBOARD
          </h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {user?.name?.split(' ')[0]}
          </p>
        </div>
        <button 
          onClick={() => navigate('/inventory', { state: { startNewCount: true } })}
          data-testid="record-inventory-btn"
          className="inline-flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-lg font-medium tracking-wide transition-all duration-200 hover:bg-primary/90 hover:-translate-y-0.5"
        >
          Record Inventory
          <CaretRight size={18} weight="bold" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-muted/30 rounded-xl border">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Funnel size={18} />
          <span className="text-sm font-medium">Filters:</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="flex-1 max-w-xs">
            <Label className="text-xs text-muted-foreground mb-1 block">Location</Label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger data-testid="location-filter" className="h-9">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 max-w-xs">
            <Label className="text-xs text-muted-foreground mb-1 block">Date Range</Label>
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger data-testid="date-filter" className="h-9">
                <CalendarBlank size={16} className="mr-2" />
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Custom Date Pickers */}
          {dateRange === "custom" && (
            <div className="flex gap-2 items-end">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-9 w-[130px] justify-start text-left font-normal" data-testid="start-date-picker">
                      <CalendarBlank size={16} className="mr-2" />
                      {customStartDate ? format(customStartDate, "MMM d, yyyy") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customStartDate}
                      onSelect={setCustomStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-9 w-[130px] justify-start text-left font-normal" data-testid="end-date-picker">
                      <CalendarBlank size={16} className="mr-2" />
                      {customEndDate ? format(customEndDate, "MMM d, yyyy") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customEndDate}
                      onSelect={setCustomEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}
        </div>
        {(selectedLocation !== "all" || dateRange !== "all") && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            className="self-end"
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Location Breakdown — COGS, Waste, Invoices, Transfers (Primary view) */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg tracking-wide flex items-center gap-2">
          <Warehouse size={20} weight="duotone" className="text-primary" />
          BY LOCATION
          {(dateRange !== "all") && (
            <Badge variant="secondary" className="text-[10px] ml-2">
              {dateRange === "custom" 
                ? `${customStartDate ? format(customStartDate, "MMM d") : '?'} - ${customEndDate ? format(customEndDate, "MMM d") : '?'}`
                : dateRange.toUpperCase()
              }
            </Badge>
          )}
        </h2>
        
        {stats?.stock_by_location?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {stats.stock_by_location.map((loc) => (
              <Card 
                key={loc.location_id} 
                data-testid={`location-card-${loc.location_id}`}
                className="bg-white border rounded-xl"
              >
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="font-heading text-sm tracking-wide text-muted-foreground flex items-center justify-between">
                    <span>{loc.name?.toUpperCase()}</span>
                    <Badge variant="outline" className="text-[10px] tabular-nums">
                      {loc.total_products} items
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0">
                  {/* Opening Inventory */}
                  {loc.opening_date && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Package size={14} className="text-indigo-600" />
                        Opening Inv ({loc.opening_date})
                      </span>
                      <span className="font-semibold tabular-nums text-indigo-600">
                        ${loc.opening_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <Receipt size={14} className="text-blue-600" />
                      Invoices ({loc.invoices_count})
                    </span>
                    <span className="font-semibold tabular-nums text-blue-600">
                      ${loc.invoices_total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <Trash size={14} className="text-destructive" />
                      Waste ({loc.waste_count})
                    </span>
                    <span className="font-semibold tabular-nums text-destructive">
                      ${loc.waste_total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <ArrowDown size={14} className="text-emerald-600" />
                      Transfers In ({loc.transfers_in_count})
                    </span>
                    <span className="font-semibold tabular-nums text-emerald-600">
                      ${loc.transfers_in_total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <ArrowUp size={14} className="text-orange-600" />
                      Transfers Out ({loc.transfers_out_count})
                    </span>
                    <span className="font-semibold tabular-nums text-orange-600">
                      ${loc.transfers_out_total?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  {/* Closing Inventory */}
                  {loc.closing_date && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Package size={14} className="text-violet-600" />
                        Closing Inv ({loc.closing_date})
                      </span>
                      <span className="font-semibold tabular-nums text-violet-600">
                        ${loc.closing_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  {/* Inventory Value (when no date range selected) */}
                  {!loc.opening_date && !loc.closing_date && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <CurrencyDollar size={14} className="text-green-600" />
                        Current Inventory
                      </span>
                      <span className="font-semibold tabular-nums text-green-600">
                        ${loc.total_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2 mt-1 bg-muted/50 -mx-4 px-4 rounded-b-lg">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <TrendUp size={14} className="text-primary" />
                      COGS
                    </span>
                    <span className="font-bold tabular-nums text-primary text-lg">
                      ${loc.cogs?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border rounded-xl">
            <CardContent className="text-center py-12">
              <Warehouse size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No locations configured</p>
              {user?.role === "admin" && (
                <Link to="/settings" className="text-primary hover:underline text-sm mt-2 inline-block">
                  Add your first location
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Card className="bg-white border rounded-xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Warehouse size={24} weight="duotone" className="text-primary" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Locations</p>
                <p className="text-2xl font-semibold tabular-nums">{stats?.total_locations || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border rounded-xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                <Package size={24} weight="duotone" className="text-secondary" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Products</p>
                <p className="text-2xl font-semibold tabular-nums">{stats?.total_products || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border rounded-xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Warning size={24} weight="duotone" className="text-destructive" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Low Stock</p>
                <p className="text-2xl font-semibold tabular-nums">{stats?.low_stock_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border rounded-xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CurrencyDollar size={24} weight="duotone" className="text-green-600" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Total Inventory Value</p>
                <p className="text-2xl font-semibold tabular-nums text-green-600">
                  ${stats?.total_inventory_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alerts */}
      {stats?.low_stock_items?.length > 0 && (
        <Card className="bg-white border rounded-xl">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
              <Warning size={20} weight="duotone" className="text-destructive" />
              LOW STOCK ALERTS
            </CardTitle>
            <div className="relative mt-2">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="low-stock-search"
                value={lowStockSearch}
                onChange={(e) => setLowStockSearch(e.target.value)}
                placeholder="Search products..."
                className="pl-10 pr-10 h-9"
              />
              {lowStockSearch && (
                <button onClick={() => setLowStockSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X size={18} />
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto space-y-3">
            {filteredLowStockItems.map((item) => (
              <div 
                key={item.product_id} 
                className="flex items-center justify-between py-3 border-b last:border-0"
                data-testid={`low-stock-item-${item.product_id}`}
              >
                <div>
                  <p className="font-medium">{item.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Threshold: {item.threshold} {item.unit}
                  </p>
                </div>
                <Badge variant="destructive" className="tabular-nums">
                  {item.quantity} {item.unit}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
